import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { WhiteLabelList } from 'src/app/_data/white-label-list';
import { LocationRegisterList } from 'src/app/_data/location-resgister';
import { UserSubscriptionInfo } from 'src/app/_models/subscription-management/user-subscription-info';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { GlobalService } from 'src/app/_services/global.service';
import { SignupService } from 'src/app/_services/signup.service';
import { SubscriptionPlanService } from 'src/app/_services/subscription-plan.service';
import { SupportService } from 'src/app/_services/support.service';
import { UserService } from 'src/app/_services/user.service';
import { PlanDataService } from 'src/app/_services/plan-data.service';
import { finalize, timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { CreateSupportLogRequest } from 'src/app/_models/support/create-support-log-request';
import {
  calculateSubscriptionRecurringTaxableSubtotal,
  calculateSubscriptionRecurringTaxAmount,
  calculateSubscriptionRecurringTotal,
  isSubscriptionAddonActive,
  readUpcomingInvoiceAmount,
  readUpcomingInvoiceTaxAmount,
} from './billing-and-plans-calculation';


interface Plan {
  id: number
  name: string;
  price: number;
  billingCycle: string;
  recommended?: boolean;
  button: string;
  url: string;
}

interface Invoice {
  invoiceNumber: string;
  date: string | Date;
  plan: string;
  recurrence?: string | number;
  charge: string | number;
  nextPayment?: string | null;
}

@Component({
  selector: 'app-billing-and-plans',
  templateUrl: './billing-and-plans.component.html',
  styleUrls: ['./billing-and-plans.component.scss'],
})
export class BillingAndPlansComponent implements OnInit {

  isMonthly: boolean = false
  _global = GlobalService
  plans: any[] = [];
  currentPlanDetails: any = null
  trialDaysLeft: number = 0;
  invoices: any[] = []

  //   Change Plan
  upgradeRequested: boolean = false;
  whiteLabelList = WhiteLabelList;
  who: any = null;
  currentPlanID: number = 0;
  changedPlanID: number = 0;

  selectedPlanForSubscription = <any>{};
  planChanged: boolean = false;

  ShowCustomConfirmation = false;
  confirmationText: any;
  CurrentAction = 0;
  ErrorMessage = '';
  currentSubscriptionCharges = 0;
  newSubscriptionCharges = 0;
  subscriptionChangeNotes = '';
  upgradeSucessMessage = '';
  supportRequestError = '';
  supportRequestSending = false;
  supportDialogVisible = false;
  // TODO re-enable: flip this back on to restore "Change Plan" as a request type in the Contact
  // Support dialog — for now only "Support" requests are accepted.
  private readonly supportChangePlanEnabled = false;
  supportTypeOptions = this.supportChangePlanEnabled
    ? [
        { label: 'Change Plan', value: 'change-plan' },
        { label: 'Support', value: 'support' },
      ]
    : [{ label: 'Support', value: 'support' }];
  selectedSupportType: 'change-plan' | 'support' = 'support';
  initialSupportType: 'change-plan' | 'support' = 'support';
  supportMessage = '';
  supportSelectedPlanId: number | null = null;
  supportPlans: any[] = [];
  expandedPlanTags: Set<string | number> = new Set();
  supportSelectedInterval: 'Monthly' | 'Yearly' | null = null;

  planData: any = null;
  currentInvoice: any = null;
  isSummaryLoading = true;
  isHistoryLoading = true;

  // Live GetSubscriptions (V1) match for the account's current plan — supplies the real,
  // plan-specific add-on catalog (subscriptionAddons) and the real "can this plan buy extra
  // registers/locations" flags (featureData's AdditionalRegister/AdditionalLocationPlus1Reg
  // isAvailable). Null until loadLivePlanExtras() resolves; subscriptionAddonsList/canBuyMoreUsage
  // fall back to the local plan-list.json catalog until then / if the request fails.
  private liveMatchedPlan: any = null;

  // The account's current plan title straight from the live catalog (GetSubscriptionsV2, matched
  // by cosmosId) — the local plan-list.json's planName can drift from the API (e.g. a plan renamed
  // "Standard" -> "Plus" server-side) since it's only a bundled fallback, not the source of truth.
  // Null until loadLiveCurrentPlanTitle() resolves; resolvedPlanName falls back to planData/local
  // catalog until then / if the request fails.
  private liveCurrentPlanTitle: string | null = null;
  constructor(
    public _signup: SignupService,
    private _router: Router,
    public _abd: AppBaseDataService,
    private activeRouter: ActivatedRoute,
    private _spinner: NgxSpinnerService,
    private _subscriptionPlanService: SubscriptionPlanService,
    private _supportService: SupportService,
    private _confirmationService: ConfirmationService,
    private _userService: UserService,
    private _planData: PlanDataService,
  ) { }

  isCancellingSubscription = false;

  /** planData?.planName from getBusinessInvoice() is unreliable/often empty — resolve the real
   *  name from the local plan catalog by the account's actual subscribed plan id instead, same
   *  fix as app.topbar.component.ts's currentPlanName. */
  get resolvedPlanName(): string {
    if (this.liveCurrentPlanTitle) return this.liveCurrentPlanTitle;
    if (this.planData?.planName) return this.planData.planName;
    const setup = this._global.userInfo?.businessSetup?.[0];
    const planCosmosId = setup?.subscription?.planId ?? setup?.subscription?.PlanId;
    if (!planCosmosId) return 'Free';
    const matchedPlan = this._planData.list.find((p: any) => String(p.cosmosId) === String(planCosmosId));
    return matchedPlan?.planName || 'Free';
  }

  /** Fetches the live GetSubscriptionsV2 entry for the account's current plan by cosmosId — same
   *  match approach ChangePlanComponent's displayPlanCards uses, so the plan name shown here always
   *  reflects the API's current title instead of the bundled plan-list.json, which is only a
   *  fallback and can go stale if a plan is renamed server-side (e.g. "Standard" -> "Plus").
   *  Silently leaves liveCurrentPlanTitle null on failure/no match, which resolvedPlanName already
   *  treats as "fall back to planData/local catalog." */
  private loadLiveCurrentPlanTitle(): void {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const planCosmosId = setup?.subscription?.planId ?? setup?.subscription?.PlanId;
    if (!planCosmosId) return;
    this._subscriptionPlanService.getSubscriptionPlanListV2().pipe(
      timeout(10000),
      catchError(() => of(null)),
    ).subscribe((res: any) => {
      const plans = res?.data ?? res?.Data ?? res ?? [];
      const matched = (Array.isArray(plans) ? plans : []).find(
        (p: any) => String(p?.cosmosId ?? p?.CosmosId ?? '') === String(planCosmosId)
      );
      const title = matched?.title ?? matched?.Title;
      if (title) this.liveCurrentPlanTitle = title;
    });
  }

  get planStatusText(): string {
    return 'Active';
  }

  get planChips(): string[] {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const chips: string[] = [];
    // registerCount/locationCount (not the raw noOfRegisters/noOfLocations fields) — those can lag
    // behind a real purchase; see those getters' doc comment.
    const registers = this.registerCount;
    const locations = this.locationCount;
    if (registers) chips.push(`${registers} register${Number(registers) === 1 ? '' : 's'}`);
    if (locations) chips.push(`${locations} location${Number(locations) === 1 ? '' : 's'}`);
    const hasOnlineStore = setup?.standardOnlineStore ?? setup?.StandardOnlineStore;
    const hasInStore = (setup?.hasPOS ?? setup?.HasPOS) || (setup?.hasPOSLite ?? setup?.HasPOSLite);
    if (hasOnlineStore) chips.push('Online store');
    if (hasInStore) chips.push('In-store');
    return chips;
  }

  // WARNING — NOT FIXED, RE-ENABLED FOR SANDBOX DEMO ONLY: "Buy hardware" for an already-
  // subscribed account routes through PaymentPageComponent.buildHardwareOnlyStripePayload(),
  // which creates a Stripe Checkout session that's still coming back as a RECURRING subscription
  // (confirmed in sandbox testing — twice, after two different attempted payload fixes) instead
  // of the intended one-time hardware charge. There is no known-working one-time-charge contract
  // anywhere in this codebase to copy from (CreateStripeSession/v2/v3 are used exclusively to
  // create subscriptions everywhere else). Re-enabled here only because the environment this is
  // pointed at is Stripe's SANDBOX (no real charges possible) for a client demo — MUST be
  // re-disabled (flip back to false) or fixed for real, with backend confirmation of the
  // one-time-charge contract, before this ever points at production Stripe. New-plan checkouts
  // that include hardware are a different, unaffected code path and don't need this flag.
  private readonly USAGE_HARDWARE_PURCHASE_ENABLED = true;

  /** Hardware (POS terminals, card readers, etc.) is only relevant for in-store selling — mirrors
   *  the checkout page's own "Hardware is used for in-store selling... Add In-store as a channel to
   *  order devices" gating. Also requires the plan itself to support hardware purchasing
   *  (hardwareDevicesEnable) — true for every current plan tier, but checked in case that ever
   *  differs by plan. */
  get hardwareSectionVisible(): boolean {
    if (!this.USAGE_HARDWARE_PURCHASE_ENABLED) return false;
    const setup = this._global.userInfo?.businessSetup?.[0];
    const hasInStore = !!((setup?.hasPOS ?? setup?.HasPOS) || (setup?.hasPOSLite ?? setup?.HasPOSLite));
    return hasInStore && !!this.matchedPlanCatalogEntry()?.hardwareDevicesEnable;
  }

  goToHardwareShop(): void {
    this._router.navigate(['/app/hardware']);
  }

  get billingCycleLabel(): string {
    if (this.isFreePlan) return 'Monthly';
    return this.planData?.subscriptionInterval === 12 ? 'Yearly' : 'Monthly';
  }

  private isValidSubscriptionDate(value: string | Date | null | undefined): boolean {
    if (!value) return false;
    const year = new Date(value).getFullYear();
    return year > 1;
  }

  get subscribedOnDate(): string | Date | null {
    if (this.isFreePlan) {
      const createdDate = this.currentPlanDetails?.createdDate;
      return this.isValidSubscriptionDate(createdDate) ? createdDate : null;
    }
    const freeTrialStartDate = this.currentPlanDetails?.subscription?.freeTrialStartDate;
    const startDate = this.currentPlanDetails?.subscription?.startDate;
    if (this.isValidSubscriptionDate(freeTrialStartDate)) return freeTrialStartDate;
    if (this.isValidSubscriptionDate(startDate)) return startDate;
    return null;
  }

  get nextBillingDate(): string | Date | null {
    if (this.isFreePlan) return null;
    return this.currentPlanDetails?.subscription?.nextChargeDate ?? null;
  }

  /** Most recent plan change (e.g. "Premium -> Plus"), from businessSetup[0]'s own
   *  lastPlanChange* fields (GETUSERBYUSERNAME/B2CGraphClientApi) — sibling fields of hasPOS/
   *  isBusinessSetup, not nested under .subscription. Null until a plan change has ever happened
   *  (fields absent) or on the Free plan. */
  get lastPlanChange(): { fromTitle: string; toTitle: string; date: string | Date } | null {
    if (this.isFreePlan) return null;
    const setup = this.currentPlanDetails;
    const fromTitle = this.readField(setup, 'lastPlanChangeFromPlanTitle', 'LastPlanChangeFromPlanTitle');
    const toTitle = this.readField(setup, 'lastPlanChangeToPlanTitle', 'LastPlanChangeToPlanTitle');
    const date = this.readField(setup, 'lastPlanChangeDate', 'LastPlanChangeDate');
    if (!fromTitle || !toTitle) return null;
    return { fromTitle, toTitle, date };
  }

  // --- Upgrade/downgrade/cancel/deactivate confirm modal, matching the LincSell Signup.dc.html
  // prototype's confirmAction state machine (title/body/note/cta copy per action). ---
  confirmAction: 'upgrade' | 'downgrade' | 'cancel' | 'deactivate' | null = null;

  get confirmModalOpen(): boolean {
    return this.confirmAction !== null;
  }

  set confirmModalOpen(value: boolean) {
    if (!value) this.confirmAction = null;
  }

  get canUpgrade(): boolean {
    const highestPlanId = this._planData.list.reduce((max: number, p: any) => {
      const id = Number(p?.id);
      return Number.isFinite(id) && id > max ? id : max;
    }, 1);
    const currentId = this.resolveCurrentPlanId();
    return currentId === null || currentId < highestPlanId;
  }

  get canDowngrade(): boolean {
    return !this.isFreePlan;
  }

  /** Matches the prototype: cancel is only offered on a paid plan (Plus/Premium equivalent). */
  get canCancelSubscription(): boolean {
    return !this.isFreePlan;
  }

  /** Matches the prototype: deactivate is only offered once already on the Free plan. */
  get canDeactivateAccount(): boolean {
    return this.isFreePlan;
  }

  get confirmTitle(): string {
    switch (this.confirmAction) {
      case 'deactivate': return 'Deactivate your account?';
      case 'cancel': return 'Cancel your subscription?';
      case 'downgrade': return 'Downgrade your plan?';
      default: return 'Upgrade your plan?';
    }
  }

  get confirmBody(): string {
    const planName = this.resolvedPlanName;
    switch (this.confirmAction) {
      case 'deactivate':
        return 'Your LincSell account will be closed. Your store goes offline and your products, orders and customer data are scheduled for deletion.';
      case 'cancel':
        return `Your ${planName} plan will be cancelled and your account moves to the Free plan. Registers, locations and paid add-ons will be removed.`;
      case 'downgrade':
        return `You're on ${planName}. Pick a lower plan on the next screen — the change takes effect at your next billing date and nothing is charged today.`;
      default:
        return `You're on ${planName}. Pick a higher plan on the next screen — you'll only be charged the prorated difference for the days left in this cycle.`;
    }
  }

  get confirmNote(): string {
    const planName = this.resolvedPlanName;
    const amount = this.isFreePlan ? '$0.00' : (this.formatCurrency(this.nextPaymentAmount) || '$0.00');
    const per = this.billingCycleLabel === 'Yearly' ? '/yr' : '/mo';
    switch (this.confirmAction) {
      case 'deactivate':
        return "This can't be undone. You'll have 30 days to reactivate by signing in again before data is permanently removed.";
      case 'cancel':
        return `This takes effect immediately. You can resubscribe at any time, but your ${planName} pricing isn't guaranteed.`;
      case 'downgrade':
        return `Current plan: ${planName} · ${amount}${per} · Next billing date ${this.nextBillingDate ? new Date(this.nextBillingDate).toDateString() : 'N/A'}`;
      default:
        return `Current plan: ${planName} · ${amount}${per} · Billed ${this.billingCycleLabel.toLowerCase()}`;
    }
  }

  get confirmCta(): string {
    switch (this.confirmAction) {
      case 'deactivate': return 'Deactivate account';
      case 'cancel': return 'Yes, cancel plan';
      default: return 'Continue to plans';
    }
  }

  get confirmDismiss(): string {
    switch (this.confirmAction) {
      case 'deactivate': return 'Keep my account';
      case 'cancel': return 'Keep my plan';
      default: return 'Not now';
    }
  }

  get confirmIsDestructive(): boolean {
    return this.confirmAction === 'cancel' || this.confirmAction === 'deactivate';
  }

  get confirmIcon(): string {
    switch (this.confirmAction) {
      case 'deactivate': return 'pi pi-power-off';
      case 'cancel': return 'pi pi-times-circle';
      case 'downgrade': return 'pi pi-arrow-down';
      default: return 'pi pi-arrow-up';
    }
  }

  doUpgrade(): void {
    if (!this.canUpgrade) return;
    this.confirmAction = 'upgrade';
  }

  doDowngrade(): void {
    if (!this.canDowngrade) return;
    this.confirmAction = 'downgrade';
  }

  requestCancelSubscription(): void {
    if (!this.canCancelSubscription) return;
    this.confirmAction = 'cancel';
  }

  requestDeactivateAccount(): void {
    if (!this.canDeactivateAccount) return;
    this.confirmAction = 'deactivate';
  }

  closeConfirm(): void {
    this.confirmAction = null;
  }

  runConfirm(): void {
    const action = this.confirmAction;
    this.confirmAction = null;
    if (action === 'cancel') {
      this.performCancelSubscription();
      return;
    }
    if (action === 'deactivate') {
      // No self-service deactivation API exists — route through the same support request flow
      // used elsewhere in this component rather than faking a destructive action.
      this.openSupportDialog();
      this.selectedSupportType = 'support';
      this.supportMessage =
        'I would like to deactivate my LincSell account. Please contact me to confirm and process this request.';
      return;
    }
    // upgrade/downgrade: hand off to the standalone plan-comparison page (matches
    // LincSell Signup.dc.html's "Continue to plans" screen — now its own route, see
    // ChangePlanComponent, instead of an inline toggle here).
    // Clear the one-time checkout lock (see PaymentPageComponent.CHECKOUT_LOCK_KEY) — this is a
    // deliberate re-entry into plan picking, not a browser Back landing on a stale checkout.
    try { sessionStorage.removeItem('checkout-just-completed'); } catch { /* ignore */ }
    this._router.navigate(['/change-plan']);
  }

  private performCancelSubscription(): void {
    if (this.isCancellingSubscription) return;
    this.isCancellingSubscription = true;
    this._abd.showSpinner();
    const isStripe =
      this._global.userInfo?.businessSetup?.[0]?.subscription?.transactionDetails?.paymentMethod === 'Stripe';
    this._subscriptionPlanService
      .cancelSubscriptionByCustomer(isStripe)
      .pipe(finalize(() => {
        this.isCancellingSubscription = false;
        this._abd.hideSpinner();
      }))
      .subscribe({
        next: (res: any) => {
          if (res?.success) {
            this.getCurrentPlanDetails();
          }
        },
      });
  }



  ngOnInit(): void {
    this.isSummaryLoading = true;
    this.isHistoryLoading = true;
    this.daysLeft()
    this.getCurrentPlanDetails()
    this.loadLiveCurrentPlanTitle()
    this.loadLivePlanExtras()
    this.loadHardwareCatalog()

  }

  /** Fetches the live GetSubscriptions (V1) entry for the account's current plan by title —
   *  same match approach PaymentPageComponent's loadUsageLimits()/mergeLiveAddons() use — so
   *  subscriptionAddonsList and canBuyMoreUsage reflect the API's real, plan-specific data instead
   *  of only the local plan-list.json catalog. Silently leaves liveMatchedPlan null on failure/no
   *  match, which both consumers already treat as "fall back to the local catalog." */
  private loadLivePlanExtras(): void {
    const planName = this.resolvedPlanName.trim().toLowerCase();
    if (!planName) return;
    this._subscriptionPlanService.getSubscriptionPlanList().pipe(
      timeout(10000),
      catchError(() => of(null)),
    ).subscribe((res: any) => {
      const plans = res?.data ?? res?.Data ?? res ?? [];
      this.liveMatchedPlan = (Array.isArray(plans) ? plans : []).find(
        (p: any) => String(p?.title ?? p?.Title ?? '').trim().toLowerCase() === planName
      ) || null;
    });
  }
  // Friendly labels for packageDetails[].packageType, used to categorize invoices whose
  // planName comes back empty (hardware / add-on purchases from GetBusinessInvoices — confirmed
  // in real invoice data where planName is "" for hardware/add-on rows but packageDetails always
  // carries the actual line items).
  private readonly packageTypeLabels: Record<string, string> = {
    Subscription: 'Subscription',
    Hardware: 'Hardware',
    AdvancedLoyaltyProgram: 'Add-on',
    BasiceCommerceStore: 'Add-on',
    AdditionalLocationPlus1Reg: 'Add-on',
    AdditionalRegister: 'Add-on',
  };

  /** Backend package titles use internal shorthand ("Additional Location + 1Reg (Monthly)",
   *  "Additional Register (Monthly)") — normalize to standard customer-facing wording before
   *  display instead of the raw string. */
  private displayLineTitle(title: string): string {
    return title
      .replace(/^Additional Location\s*\+\s*1\s*Reg\b/i, 'Location')
      .replace(/^Additional Register\b/i, 'Register');
  }

  /** Display order for invoice line items: plan first, then Location, then Register, then
   *  everything else (add-ons) in their original order. */
  private lineItemRank(packageType: string): number {
    if (packageType === 'Subscription') return 0;
    if (packageType === 'AdditionalLocationPlus1Reg') return 1;
    if (packageType === 'AdditionalRegister') return 2;
    return 3;
  }

  /** planName/grandTotal from GetBusinessInvoices are unreliable for non-subscription invoices
   *  (confirmed: hardware/add-on purchases come back with planName: "" and, for the plan invoice
   *  itself, grandTotal: 0 despite a real planCost) — packageDetails[] always carries the real
   *  line items, so derive the display name/category/charge from there when the top-level fields
   *  are empty/zero instead of showing a blank plan or a $0.00 charge. */
  private describeInvoice(val: any): { plan: string; items: string[]; lineItems: { name: string; qty: number; price: number }[]; category: string; charge: number; actualCharged: number; taxAmount: number; isOneTime: boolean } {
    const packages: any[] = Array.isArray(val.packageDetails) ? val.packageDetails : [];
    // Kept as a list rather than a single joined string — a multi-line-item invoice (e.g. two
    // add-ons plus register/location bumps) reads as an unreadable comma-separated wall of text
    // when flattened; the template renders items one-per-line with a collapse toggle instead.
    const items = [...new Set(
      packages
        .slice()
        // Plan first, then Location, then Register, then add-ons — stable sort preserves
        // original relative order within each rank.
        .sort((a, b) => this.lineItemRank(a?.packageType) - this.lineItemRank(b?.packageType))
        .map((p) => {
          // The Subscription-type row's own title can come back blank from GetBusinessInvoices
          // (same unreliable-for-non-primary-lines pattern this function's doc comment already
          // notes for val.planName/grandTotal) — without a fallback it silently drops out of the
          // items list entirely, even though it's still counted in `category` below, leaving an
          // invoice that reads "Subscription + Add-on" but only ever lists the add-on lines.
          const title = this.displayLineTitle(String(p?.title || '').trim()
            || (p?.packageType === 'Subscription' ? (val.planName || 'Subscription') : (this.packageTypeLabels[p?.packageType] || '')));
          if (!title) return '';
          const qty = this.toNumber(p?.quantity);
          return qty > 1 ? `${title} ×${qty}` : title;
        })
        .filter(Boolean)
    )];
    // Same title/qty derivation as `items` above, but kept as structured {name, qty, price} rows
    // (price = the line's own subTotalCost) so the receipt popup can show a proper qty/price
    // breakdown instead of a single "Title ×qty" string.
    const lineItemsMap = new Map<string, { name: string; qty: number; price: number; rank: number }>();
    packages.forEach((p) => {
      const title = this.displayLineTitle(String(p?.title || '').trim()
        || (p?.packageType === 'Subscription' ? (val.planName || 'Subscription') : (this.packageTypeLabels[p?.packageType] || '')));
      if (!title) return;
      const qty = this.toNumber(p?.quantity) || 1;
      const price = this.toNumber(p?.subTotalCost);
      const rank = this.lineItemRank(p?.packageType);
      const existing = lineItemsMap.get(title);
      if (existing) {
        existing.qty += qty;
        existing.price += price;
      } else {
        lineItemsMap.set(title, { name: title, qty, price, rank });
      }
    });
    // Plan first, then Location, then Register, then add-ons.
    const lineItems = [...lineItemsMap.values()]
      .sort((a, b) => a.rank - b.rank)
      .map(({ name, qty, price }) => ({ name, qty, price }));
    const categories = [...new Set(packages.map((p) => this.packageTypeLabels[p?.packageType] || p?.packageType).filter(Boolean))];
    const category = categories.join(' + ') || 'Subscription';

    const plan = val.planName || items[0] || `${category} charge`;

    // grandTotal is the only field that reflects what was actually charged — planCost and the
    // packageDetails sum are catalog/line prices used purely as a display fallback when
    // grandTotal comes back 0 for reasons unrelated to payment (see doc comment above), so they
    // must never be treated as evidence that money was collected.
    const actualCharged = this.toNumber(val.grandTotal);
    const charge = actualCharged > 0
      ? val.grandTotal
      : this.toNumber(val.planCost) || packages.reduce((sum, p) => sum + this.toNumber(p?.subTotalCost), 0);

    // Hardware (packageType: "Hardware") is a one-time device purchase, not a recurring billing
    // line — GetBusinessInvoices still stamps a nextChargeDate on these rows, but there's no
    // actual future charge to show, so an invoice made up of only hardware lines has no
    // recurrence/next payment.
    const isOneTime = packages.length > 0 && packages.every((p) => p?.packageType === 'Hardware');

    const taxAmount = this.toNumber(val.totalTaxAmount);

    return {
      plan,
      items: items.length ? items : [plan],
      lineItems: lineItems.length ? lineItems : [{ name: plan, qty: 1, price: charge }],
      category,
      charge,
      actualCharged,
      taxAmount,
      isOneTime,
    };
  }

  getInvoices() {
    try {
      this.isHistoryLoading = true;
      this._abd.showSpinner()
      this._subscriptionPlanService.getBusinessBillingInvoices().subscribe((res: UserSubscriptionInfo | any) => {
        this._abd.hideSpinner()
        if (res.success) {
          const mappedInvoices = res.data
            .map((val) => {
              const { plan, items, lineItems, category, charge, actualCharged, taxAmount, isOneTime } = this.describeInvoice(val);
              return {
                invoiceNumber: val.invoiceNo,
                date: val.dateTime,
                stripeLink: val.stripeLink || null,
                plan,
                items,
                lineItems,
                category,
                taxAmount,
                recurrence: val.planInterval,
                charge,
                nextPayment: val.nextChargeDate,
                isOneTime,
                // paymentFinalized/totalPaid reflect the actual Stripe charge outcome — a
                // postedStatus of "Posted" only means the invoice row was recorded, not that
                // money was collected (e.g. a failed charge that still got posted).
                // GetBusinessInvoices (the list this table renders) doesn't carry
                // paymentFinalized/totalPaid at all — only the richer single-invoice document
                // does. So default to paid (matches this endpoint's existing "Posted" rows,
                // which are all settled sales) and only flip to Unpaid when the field is present
                // and explicitly false. A $0 invoice (Free plan, comp) has nothing owed, so it's
                // always paid regardless of paymentFinalized.
                isPaid: actualCharged === 0 || this.readField(val, 'paymentFinalized', 'PaymentFinalized') !== false,
                expanded: false,
              };
            })
            .sort(
              (a, b) => new Date(b.date as any).getTime() - new Date(a.date as any).getTime()
            );
          // Backend stamps every invoice row with the account's single current nextChargeDate,
          // so old recurring invoices from before a plan/add-on change (e.g. a superseded $212
          // "Plus + Advanced Loyalty Program" charge) show the same future date as the invoice
          // for the plan actually billing next. Only the newest recurring invoice reflects a
          // real upcoming charge — older recurring rows are historical and won't recur as billed.
          let sawRecurring = false;
          mappedInvoices.forEach((inv) => {
            if (inv.isOneTime) return;
            if (sawRecurring) {
              inv.nextPayment = null;
            } else {
              sawRecurring = true;
            }
          });
          this.invoices = mappedInvoices;
        }
        this.isHistoryLoading = false;
      }, () => {
        this._abd.hideSpinner()
        this.isHistoryLoading = false;
      })
    } catch (error) {
      this.isHistoryLoading = false;
    }
  }
  /** Links to the merchant's own Stripe Dashboard payment record (dashboard.stripe.com/payments/
   *  {payment_intent_id}) — a real, resolvable Stripe URL for whoever's logged into this account's
   *  Stripe dashboard. Not a customer-facing receipt link: GetBusinessInvoices never returns a real
   *  Charge.receipt_url (checked — no such field exists anywhere in the invoice response), so a
   *  pay.stripe.com/receipts/... link can't be constructed client-side; that's why the invoiceNo's
   *  embedded payment-intent id is pointed at the dashboard instead. */
  buildStripeDashboardUrl(invoiceNumber: string): string | null {
    if (!invoiceNumber) return null;
    const match = invoiceNumber.match(/(pi_[a-zA-Z0-9]+)/);
    return match ? `https://dashboard.stripe.com/payments/${match[1]}` : null;
  }

  /** Customer-facing Stripe receipt link — GetBusinessInvoices now returns a real
   *  pay.stripe.com/receipts/... URL per invoice (stripeLink). Falls back to the merchant-only
   *  dashboard link above only for older invoices fetched before that field existed. */
  getStripeReceiptUrl(invoice: any): string | null {
    return invoice?.stripeLink || this.buildStripeDashboardUrl(invoice?.invoiceNumber);
  }

  getRecurrence(value: any) {
    if (value === 1) {
      return "Monthly"
    } else if (value === 12) {
      return "Yearly"
    } else {
      return "-"
    }
  }

  selectedInvoice: any = null;

  get invoiceReceiptOpen(): boolean {
    return this.selectedInvoice !== null;
  }

  set invoiceReceiptOpen(value: boolean) {
    if (!value) this.selectedInvoice = null;
  }

  openInvoiceReceipt(invoice: any): void {
    this.selectedInvoice = invoice;
  }

  // Backend stamps unset next-charge dates as DateTime.MinValue, which serializes as
  // "2/1/0001" (or similarly year <= 1) — treat that as no next payment rather than showing it.
  // Read the year directly out of the string instead of `new Date(value).getFullYear()`: browsers
  // parse an M/D/YYYY string with an ambiguous fragment inconsistently (e.g. "2/1/0001" comes back
  // as year 2001 in some engines), which let the sentinel slip past a `year > 1900` check.
  isValidNextPayment(value: any): boolean {
    if (!value) return false;
    const str = String(value).trim();
    const slashMatch = str.match(/^\d{1,2}\/\d{1,2}\/(\d{1,4})/);
    if (slashMatch) {
      return parseInt(slashMatch[1], 10) > 1900;
    }
    const isoMatch = str.match(/^(\d{1,4})-\d{1,2}-\d{1,2}/);
    if (isoMatch) {
      return parseInt(isoMatch[1], 10) > 1900;
    }
    const year = new Date(str).getFullYear();
    return !isNaN(year) && year > 1900;
  }

  get isFreePlan(): boolean {
    const planName = (this.planData?.planName || '').toString().trim().toLowerCase();
    if (planName === 'free') return true;
    // resolveCurrentPlanId() parses businessSetup[0].subscription.planId, which is a cosmosId
    // string (e.g. "1628516995007"), not the local catalog's small 1-4 id — so `=== 1` never
    // actually matches for a real account (same mismatch matchedPlanCatalogEntry()'s own doc
    // comment already calls out). Match by cosmosId via the catalog instead, same as
    // resolvedPlanName/matchedPlanCatalogEntry(); resolveCurrentPlanId() stays as a last-resort
    // fallback for whatever cases it might still legitimately apply to.
    const matched = this.matchedPlanCatalogEntry();
    if (matched) {
      return Number(matched.id) === 1 || String(matched.planName || '').trim().toLowerCase() === 'free';
    }
    const planId = this.resolveCurrentPlanId();
    return planId === 1;
  }

  get nextPaymentAmount(): number {
    const invoiceAmount = readUpcomingInvoiceAmount(this.currentInvoice);
    if (invoiceAmount > 0) return invoiceAmount;
    const subscription = this.currentPlanDetails?.subscription;
    if (!subscription) return 0;
    const calculatedRecurringTotal = calculateSubscriptionRecurringTotal(
      subscription,
      this.subscriptionAddonsList,
      this.subscriptionTaxRate
    );
    if (calculatedRecurringTotal > 0) return calculatedRecurringTotal;
    const base = this.toNumber(subscription.totalCost);
    const support = this.toNumber(subscription.totalSupportPkgCost);
    const additional = this.toNumber(subscription.totalAdditionalServicesCost);
    return base + support + additional;
  }

  /** Plan price shown in the hero — the pre-tax subtotal, distinct from nextPaymentAmount (which
   *  includes tax) so the hero and the "Total due next (incl. tax)" stat don't display the same
   *  number twice. */
  get planPriceAmount(): number {
    return Math.max(0, this.nextPaymentAmount - this.nextPaymentTaxAmount);
  }

  /** Raw plan subscription cost only (e.g. subscription.planCost) — excludes addons, packages and tax. */
  get rawPlanCost(): number {
    const subscription = this.currentPlanDetails?.subscription;
    return this.toNumber(subscription?.planCost ?? subscription?.PlanCost);
  }

  get nextPaymentTaxAmount(): number {
    const invoiceTax = readUpcomingInvoiceTaxAmount(this.currentInvoice);
    if (invoiceTax > 0) return invoiceTax;
    const subscription = this.currentPlanDetails?.subscription;
    if (!subscription) return 0;
    return calculateSubscriptionRecurringTaxAmount(
      subscription,
      this.subscriptionAddonsList,
      this.subscriptionTaxRate
    );
  }

  get nextPaymentTaxableBase(): number {
    const subscription = this.currentPlanDetails?.subscription;
    if (!subscription) return 0;
    return calculateSubscriptionRecurringTaxableSubtotal(subscription, this.subscriptionAddonsList);
  }

  get subscriptionTaxRate(): number {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const subscription = setup?.subscription ?? setup?.Subscription ?? {};
    const explicitRate = this.toNumber(subscription?.totalTaxValue ?? subscription?.TotalTaxValue);
    if (explicitRate > 0) return explicitRate;

    const state =
      this.readField(setup, 'state', 'State') ??
      this.readField(setup, 'businessState', 'BusinessState') ??
      this.readField(setup, 'companyState', 'CompanyState');
    return String(state || '').trim().toUpperCase() === 'TX' ? 8.25 : 0;
  }

  private resolveCurrentPlanId(): number | null {
    const rawId =
      this.planData?.planId ??
      this.planData?.planID ??
      this.currentPlanDetails?.subscription?.planId ??
      this.currentPlanDetails?.subscription?.planID ??
      this.currentPlanDetails?.subscription?.PlanId ??
      this.currentPlanDetails?.subscription?.PlanID ??
      null;
    const parsed = parseInt(rawId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private readField(source: any, camelKey: string, pascalKey: string): any {
    return source?.[camelKey] ?? source?.[pascalKey];
  }

  /** The account's own catalog entry (register/location defaults, addons) for whichever plan
   *  they're actually subscribed to — same id-matching PaymentPageComponent uses when priming its
   *  checkout state, so the numbers shown here line up with what /check-out will show. */
  private matchedPlanCatalogEntry(): any {
    // businessSetup[0].subscription.planId is a cosmosId, not the small 1-4 `id` the local
    // catalog uses internally — resolveCurrentPlanId() parses the latter (for comparing against
    // getSubscriptionPlanListV2()'s numeric planId) and would never match here. Match by cosmosId
    // instead, same lookup resolvedPlanName above already relies on, with a name fallback matching
    // PaymentPageComponent.resolvePlan()'s own catalog-matching order.
    const setup = this._global.userInfo?.businessSetup?.[0];
    const planCosmosId = setup?.subscription?.planId ?? setup?.subscription?.PlanId;
    return (
      this._planData.list.find((p: any) => planCosmosId != null && String(p.cosmosId) === String(planCosmosId)) ||
      this._planData.list.find((p: any) => String(p.planName).toLowerCase() === this.resolvedPlanName.toLowerCase()) ||
      null
    );
  }

  /** Billable-extra qty from SubscribedFeatures, keyed by the backend's Type code — Type 6 =
   *  AdditionalRegister, Type 7 = AdditionalLocationPlus1Reg. Post-fix, each entry's Qty is already
   *  the correct absolute extra count (e.g. plan allows 1 free location, account has 3 total →
   *  Qty = 2) — no arithmetic needed here, just sum active entries of the matching type. Falls back
   *  to the pre-fix title/isAddon heuristic when no entry carries a `type`/`Type` field at all,
   *  which covers stale Cosmos documents from before the one-time v3/subscription/update
   *  correction pass (see project notes) — once every account has been re-saved this fallback is
   *  dead code and can be deleted. */
  private billableExtraQtyFromFeatures(setup: any, type: 6 | 7, legacyTitleIncludes: string): number {
    const features: any[] =
      setup?.subscription?.subscribedFeatures ??
      setup?.Subscription?.SubscribedFeatures ??
      [];
    const hasTypedEntries = features.some((f: any) => (f?.type ?? f?.Type) != null);
    if (hasTypedEntries) {
      return features.reduce((sum: number, f: any) => {
        const isActive = f?.isActive ?? f?.IsActive;
        const featureType = this.toNumber(f?.type ?? f?.Type);
        if (!isActive || featureType !== type) return sum;
        return sum + this.toNumber(f?.qty ?? f?.Qty);
      }, 0);
    }
    return features.reduce((sum: number, f: any) => {
      const title = String(f?.title ?? f?.Title ?? '').trim().toLowerCase();
      const isActive = f?.isActive ?? f?.IsActive;
      const isAddon = f?.isAddon ?? f?.IsAddon;
      if (!isActive || !isAddon || !title.includes(legacyTitleIncludes)) return sum;
      return sum + this.toNumber(f?.qty ?? f?.Qty);
    }, 0);
  }

  /** Total register count is the account-level total (businessSetup.PerNoOfRegister), not a
   *  included-plus-extra sum — per-spec source of truth for "how many registers does this account
   *  have." Falls back to noOfRegisters/1 if perNoOfRegister is missing (older accounts / pre-fix
   *  documents that never had it populated). */
  get registerCount(): number {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const perNoOfRegister = this.toNumber(this.readField(setup, 'perNoOfRegister', 'PerNoOfRegister'));
    if (perNoOfRegister > 0) return perNoOfRegister;
    return this.toNumber(this.readField(setup, 'noOfRegisters', 'NoOfRegisters')) || 1;
  }

  /** Total location count is the account-level total (businessSetup.NoOfLocations) — see
   *  registerCount's doc comment for why this reads the flat total rather than included+extra. */
  get locationCount(): number {
    const setup = this._global.userInfo?.businessSetup?.[0];
    return this.toNumber(this.readField(setup, 'noOfLocations', 'NoOfLocations')) || 1;
  }

  get includedRegisterCount(): number {
    return this.toNumber(this.matchedPlanCatalogEntry()?.defaultRegister) || 1;
  }

  get includedLocationCount(): number {
    return this.toNumber(this.matchedPlanCatalogEntry()?.defaultLocation) || 1;
  }

  /** Billable extra registers — prefers Type 6 (AdditionalRegister) SubscribedFeatures Qty (the
   *  backend's own billed figure) over a locally-computed registerCount − includedRegisterCount,
   *  since the plan's defaultRegister here is only the local plan-list.json catalog's notion of
   *  "included," which can drift from whatever the backend actually billed against. */
  get extraRegisterCount(): number {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const billed = this.billableExtraQtyFromFeatures(setup, 6, 'regist');
    if (billed > 0) return billed;
    return Math.max(0, this.registerCount - this.includedRegisterCount);
  }

  /** Billable extra locations — see extraRegisterCount's doc comment; Type 7 =
   *  AdditionalLocationPlus1Reg. */
  get extraLocationCount(): number {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const billed = this.billableExtraQtyFromFeatures(setup, 7, 'location');
    if (billed > 0) return billed;
    return Math.max(0, this.locationCount - this.includedLocationCount);
  }

  // Real per-extra pricing, not fabricated — the same catalog entries PaymentPageComponent
  // already prices "Number of Register"/"Number of Location" line items from.
  readonly registerExtraPrice = LocationRegisterList.find((i) => i.id === 1)?.price ?? 49;
  readonly locationExtraPrice = LocationRegisterList.find((i) => i.id === 2)?.price ?? 79;

  /** Which add-ons even apply is plan-specific and, like PaymentPageComponent's optionalAddons,
   *  prefers the live GetSubscriptions match's subscriptionAddons (real, plan-curated add-on list
   *  and pricing) over the local plan-list.json catalog — falling back to the local catalog only
   *  until loadLivePlanExtras() resolves or if it fails. A local entry matched by name still fills
   *  in a `detail`/id the live entry might be missing. "included" comes from
   *  Subscription.SubscribedFeatures (matched by title, IsActive: true) — confirmed against
   *  FinishSignUp.cs: the flat businessSetup[0] booleans this used to read (advancedLoyaltyProgram/
   *  customOnlineStore) mean "this business has the feature gate enabled," which gets set true for
   *  *every* paid-plan signup regardless of whether the add-on was actually purchased — they are
   *  NOT a purchase-confirmation signal. SubscribedFeatures is the only field that tracks what was
   *  specifically bought and paid for. */
  get subscriptionAddonsList(): any[] {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const localAddons = this.matchedPlanCatalogEntry()?.addons || [];
    const liveAddons: any[] | null = Array.isArray(this.liveMatchedPlan?.subscriptionAddons)
      ? this.liveMatchedPlan.subscriptionAddons
      : null;

    let planAddons: any[];
    if (liveAddons) {
      const localByName = new Map<string, any>(
        localAddons.map((a: any) => [String(a.planName || '').trim().toLowerCase(), a])
      );
      planAddons = liveAddons.filter((a: any) => a?.title).map((a: any) => {
        const name = String(a.title).trim();
        const localMatch = localByName.get(name.toLowerCase());
        return {
          id: a.id ?? localMatch?.id,
          planName: name,
          detail: a.detail || localMatch?.detail || name,
          monthlyPrice: this.toNumber(a.monthlyPrice),
          yearlyPrice: this.toNumber(a.yearlyPrice ?? a.monthlyPrice),
          defaultAddedInSubscription: a.defaultAddedInSubscription ?? localMatch?.defaultAddedInSubscription ?? false,
        };
      });
    } else {
      planAddons = localAddons;
    }

    return planAddons.filter((a: any) => !a.defaultAddedInSubscription).map((addon: any) => {
      return {
        ...addon,
        included: this.isAddonPurchased(setup, addon),
        // Custom Online Store's cost varies with the amount of customization involved, so its
        // listed price is a starting point rather than a flat rate — see usage-addon-price in the
        // template. Same rule PaymentPageComponent's mergeLiveAddons() applies on checkout.
        priceIsStartingAt: String(addon.planName || '').trim().toLowerCase() === 'custom online store',
      };
    });
  }

  /** Checked two ways, since either alone can miss a real purchase — see
   *  PaymentPageComponent.isAddonAlreadyPurchased()'s doc comment for the confirmed real-account
   *  case this is guarding against (a paid add-on whose SubscribedFeatures activation silently
   *  failed on the backend):
   *  1. Subscription.SubscribedFeatures[], matched by title (case-insensitive) with isActive: true
   *     — the backend's own purchase-confirmation record.
   *  2. Subscription.SubscriptionPlanPkg[], matched by packageId === addon.id — the actual billed
   *     transaction line items, which can be true even when SubscribedFeatures activation failed. */
  private isAddonPurchased(setup: any, addon: any): boolean {
    const subscription = setup?.subscription ?? setup?.Subscription ?? {};
    return isSubscriptionAddonActive(subscription, addon);
  }

  /** subscriptionAddonsList is a getter that maps a fresh array of fresh objects on every call —
   *  necessary since it derives from live account flags — but without trackBy, Angular's default
   *  *ngFor identity check (by object reference) sees every change-detection pass as "all rows
   *  removed, all rows re-added" and destroys/recreates the row DOM constantly. A mousedown on the
   *  Add button is itself enough to trigger that recreation, which can tear down the exact button
   *  element before its paired click event fires — silently swallowing the click. Track by the
   *  addon's stable id instead so Angular reuses the existing DOM when the same addon re-renders. */
  trackAddonById(_index: number, addon: any): any {
    return addon.id;
  }

  get activeAddonCount(): number {
    return this.subscriptionAddonsList.filter((a) => a.included).length;
  }

  /** Hardware catalog id -> title, from the same GetHardwarePackagesv2 endpoint
   *  HardwareShopComponent shops against — needed because subscriptionPlanPkg line items only
   *  carry a packageId, not a display name. Populated by loadHardwareCatalog(); purchasedHardwareList
   *  falls back to the raw packageId until this resolves. */
  private hardwareCatalogNames = new Map<string, string>();

  private loadHardwareCatalog(): void {
    this._signup.getHardwarePlanListv2().pipe(
      timeout(10000),
      catchError(() => of(null)),
    ).subscribe((res: any) => {
      if (!res?.success || !Array.isArray(res.data)) return;
      const map = new Map<string, string>();
      for (const group of res.data) {
        for (const item of (Array.isArray(group?.items) ? group.items : [])) {
          if (item?.id != null) map.set(String(item.id), item.title);
        }
      }
      this.hardwareCatalogNames = map;
    });
  }

  /** Already-purchased hardware (POS terminals, card readers, accessories). GetBusinessInvoice's
   *  planData is unreliable — confirmed null for real accounts in testing — so this reads
   *  businessSetup[0].subscription.subscriptionPlanPkg instead (the same purchased-line-items array
   *  billing-and-plans-calculation.ts's packageLineById()/matchingPackageLine() already read for
   *  register/location/addon pricing). Hardware lines are identified by packageType === 1 (confirmed
   *  against real purchase data — registers/locations are packageType 0) OR by packageId matching
   *  the hardware catalog (covers older purchases recorded before packageType was set), always
   *  excluding the register/location packageIds ('1'/'2') to be safe either way. */
  get purchasedHardwareList(): any[] {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const lines: any[] = setup?.subscription?.subscriptionPlanPkg ?? setup?.Subscription?.SubscriptionPlanPkg ?? [];
    return lines
      .filter((line: any) => {
        const packageId = String(line?.packageId ?? line?.PackageId ?? '');
        if (packageId === '1' || packageId === '2') return false;
        const packageType = this.toNumber(line?.packageType ?? line?.PackageType);
        return packageType === 1 || this.hardwareCatalogNames.has(packageId);
      })
      .map((line: any) => {
        const packageId = String(line?.packageId ?? line?.PackageId ?? '');
        return {
          name: this.hardwareCatalogNames.get(packageId) || packageId,
          qty: this.toNumber(line?.qty ?? line?.Qty),
          price: this.toNumber(line?.totalPrice ?? line?.TotalPrice ?? line?.subTotalCost ?? line?.SubTotalCost),
          packageId: line?.id ?? line?.Id ?? packageId,
        };
      });
  }

  trackHardwareByName(_index: number, hardware: any): any {
    return hardware.packageId ?? hardware.name;
  }

  getAddonPrice(addon: any): number {
    return this.isMonthly ? this.toNumber(addon.monthlyPrice) : this.toNumber(addon.yearlyPrice);
  }

  /** "Buy more" (registers/locations) and an add-on's "Add" both route through the same checkout
   *  page PaymentPageComponent already exposes for picking a plan — primed with the account's
   *  current plan (same plan, not an upgrade/downgrade) plus a one-shot "usage intent" that page
   *  reads via consumeUsageIntent() to pre-select the specific register/location/add-on being
   *  requested and to suppress its hardware step, instead of landing on a blank cart. */
  private goToUsageCheckout(intent: { bumpRegister?: boolean; bumpLocation?: boolean; presetAddonId?: any }): void {
    const plan = this.matchedPlanCatalogEntry();
    if (!plan) {
      this._router.navigate(['/change-plan']);
      return;
    }
    this._signup.selectedPlanDetails = plan;
    localStorage.setItem('selectedPlanDetails', JSON.stringify(plan));
    sessionStorage.setItem(
      'checkout-usage-intent',
      JSON.stringify({
        skipHardware: true,
        bumpRegister: !!intent.bumpRegister,
        bumpLocation: !!intent.bumpLocation,
        presetAddonId: intent.presetAddonId ?? null,
      })
    );
    this._router.navigate(['/check-out']);
  }

  /** Whether "Buy more" shows at all. Prefers the live GetSubscriptions match's real "Locations"/
   *  "Registers" featureData entries — their `addMore`/`unlimited` flags are meant to be the API's
   *  current, actively-maintained answer to "can this plan buy extra registers/locations" (same
   *  entries PaymentPageComponent's parseLimitByTypeString() reads for the purchase cap) — but
   *  confirmed live on a real Plus-plan account (which had already purchased extra registers/
   *  locations under this exact plan, so the capability is definitely real) still coming back
   *  addMore:false/unlimited:false and hiding the button entirely, matching payment-page.component
   *  .ts's ENFORCE_LIVE_USAGE_LIMITS TODO that flags this same featureData as still unreliable.
   *  Never lets that stale live "false" override a known-true signal — ORs it with the local
   *  plan-list.json catalog's additionalRegistersPurchase/additionalLocationsPurchase, same
   *  either-source-wins pattern PaymentPageComponent.canBuyExtraFor() already uses for the same
   *  reason. */
  get canBuyMoreUsage(): boolean {
    const plan = this.matchedPlanCatalogEntry();
    const localFlag = !!(plan?.additionalRegistersPurchase || plan?.additionalLocationsPurchase);
    if (this.liveMatchedPlan) {
      const featureData: any[] = Array.isArray(this.liveMatchedPlan.featureData) ? this.liveMatchedPlan.featureData : [];
      const registersEntry = featureData.find((f: any) => f?.typeString === 'Registers');
      const locationsEntry = featureData.find((f: any) => f?.typeString === 'Locations');
      const canBuyRegister = registersEntry?.unlimited === true || registersEntry?.addMore === true;
      const canBuyLocation = locationsEntry?.unlimited === true || locationsEntry?.addMore === true;
      return canBuyRegister || canBuyLocation || localFlag;
    }
    return localFlag;
  }

  buyMoreRegistersAndLocations(): void {
    // Lands on checkout at the plan's included defaults (1 register/1 location, or whatever the
    // plan's own baseline is) — the merchant increments whichever they actually want themselves via
    // the +/- steppers there, rather than checkout guessing and pre-adding one of each for them.
    this.goToUsageCheckout({});
  }

  addSubscriptionAddon(addon: any): void {
    this.goToUsageCheckout({ presetAddonId: addon?.id });
  }

  getCurrentPlanDetails() {
    try {
      this._abd.showSpinner()
      this._subscriptionPlanService.getBusinessInvoice().subscribe(
        (res: any) => {
          this.currentPlan(true)
          if (res.responseStatusCode == 903) {
            this.planData = res.data.currentSubscription;
            this.currentInvoice = res.data.invoice || null;
            this.isMonthly = (this.planData?.subscriptionInterval === 1 ? true : false)
          }
          this.isSummaryLoading = false;
        },
        (error) => {
          this.currentPlan(true)
          this._abd.hideSpinner()
          this.isSummaryLoading = false;
        }
      );
    } catch (error) {
      this._abd.hideSpinner()
      this.currentPlan(true)
      this.isSummaryLoading = false;
    }
  }

  // Invoice numbers to hide from the billing history table — e.g. a duplicate/erroneous
  // charge the account owner doesn't want shown in their invoice list.
  private readonly hiddenInvoiceNumbers = new Set<string>(['28791787300154661']);

  getDisplayInvoices() {
    if (this.invoices && this.invoices.length > 0) {
      return this.invoices.filter(
        (invoice) => !this.hiddenInvoiceNumbers.has(String(invoice.invoiceNumber))
      );
    }


  }

  

  currentPlan(value = false) {
    this._abd.showSpinner()
    try {
      this._subscriptionPlanService.getSubscriptionPlanListV2().subscribe((res) => {
        if (value === true) {
          this.getInvoices()
        } else {
          this._abd.hideSpinner()
        }
        if (res.success) {
          const normalizedPlans = (res.data || []).map((plan: any) => ({
            ...plan,
            prices: this.buildPlanPrices(plan),
            popularPlan: !!(plan.popularPlan ?? plan.PopularPlan)
          }));

          this.plans = normalizedPlans
            .filter((p) => this.isSubscriptionPlan(p))
            .filter((p) => this.isPaidPlan(p))
            .filter((p) => !this.isAddOn(p))
            .map((item) => {
              const isCurrentPlan = item.planId === this.planData?.planId;
              return {
                ...item,
                selectedPrice: this.extractPlanPrice(item),
                button: isCurrentPlan
                  ? 'Your Current Plan'
                  : 'Upgrade to ' + (item?.title || '').toLowerCase(),
                selected: isCurrentPlan
              };
            });
        }

      })
    } catch (error) {
      this._abd.hideSpinner()
      this.getInvoices()
    }

  }


  private fetchSupportPlans() {
    this._abd.showSpinner();
    this._subscriptionPlanService
      .getSubscriptionPlanListV2()
      .pipe(finalize(() => this._abd.hideSpinner()))
      .subscribe((res) => {
        if (res.success) {
          const normalized = (res.data || []).map((p: any) => ({
            ...p,
            prices: this.buildPlanPrices(p),
            popularPlan: !!(p.popularPlan ?? p.PopularPlan)
          }));
          const currentPlanId = this.resolveCurrentPlanId();
          this.supportPlans = normalized
            .filter((p: any) => this.isSubscriptionPlan(p))
            .filter((p: any) => this.isPaidPlan(p))
            .filter((p: any) => !this.isAddOn(p))
            .filter((p: any) => {
              const planId = p?.planId ?? p?.id;
              if (currentPlanId == null) return true;
              return String(planId) !== String(currentPlanId);
            });
        }
      });
  }

  private buildPlanPrices(plan: any): any[] {
    if (!plan) {
      return [];
    }
    if (Array.isArray(plan.prices) && plan.prices.length) {
      return plan.prices;
    }

    const prices: any[] = [];
    const monthly = Number(plan.monthlyPrice ?? plan.price);
    if (!isNaN(monthly) && monthly > 0) {
      prices.push({ periodString: 'Monthly', value: monthly });
    }

    const yearly = Number(plan.yearlyPrice);
    if (!isNaN(yearly) && yearly > 0) {
      prices.push({ periodString: 'Yearly', value: yearly });
    }

    return prices;
  }

  private isSubscriptionPlan(plan: any): boolean {
    return plan?.isSubscription === true || plan?.IsSubscription === true;
  }

  private isAddOn(plan: any): boolean {
    // Product type string or numeric markers that indicate add-ons.
    const type = (plan?.productType || '').toString().trim().toLowerCase();
    return (
      type === 'addon' ||
      type === 'add-on' ||
      type === 'add on' ||
      plan?.productType === 'AddOn' ||
      plan?.productType === 2 ||
      plan?.productType === 3
    );
  }

  private extractPlanPrice(plan: any) {
    if (!plan) {
      return null;
    }
    const prices = this.buildPlanPrices(plan);
    if (prices.length) {
      return (
        prices.find(
          (p: any) =>
            p.periodString === (this.isMonthly ? 'Monthly' : 'Yearly')
        ) || prices[0]
      );
    }
    return null;
  }

  private formatUsd(value: number): string {
    return Number(value || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  getSupportPlanPrice(plan: any): string {
    const preferred = this.extractPlanPrice(plan);
    if (!preferred) return this.formatUsd(0);
    const value = preferred?.value || 0;
    const period = preferred?.periodString ? ` / ${preferred.periodString}` : '';
    return `${this.formatUsd(value)}${period}`;
  }

  getSupportPlanInterval(plan: any): string {
    const preferred = this.extractPlanPrice(plan);
    return preferred?.periodString || 'N/A';
  }

  getSupportPlanPriceByPeriod(plan: any, period: 'Monthly' | 'Yearly'): string {
    const prices = this.buildPlanPrices(plan);
    if (!prices.length) return this.formatUsd(0);
    const price = prices.find((p: any) => p.periodString === period);
    if (!price) return this.formatUsd(0);
    return `${this.formatUsd(price.value)} / ${period}`;
  }

  getSupportPlanPriceNumber(plan: any, period: 'Monthly' | 'Yearly'): number {
    const prices = this.buildPlanPrices(plan);
    if (!prices.length) return 0;
    const price = prices.find((p: any) => p.periodString === period);
    return Number(price?.value) || 0;
  }

  get selectedSupportPlan(): any {
    return this.supportPlans.find((p) => p.planId === this.supportSelectedPlanId);
  }

  private getPlanPriceValue(plan: any): number {
    const price = this.extractPlanPrice(plan);
    return price?.value ?? 0;
  }

  private isPaidPlan(plan: any): boolean {
    if (!plan || plan.isTrial || plan.isCustom) return false;
    return this.getPlanPriceValue(plan) > 0;
  }

  openSupportDialog() {
    this.supportDialogVisible = true;
    this.supportRequestError = '';
    this.supportMessage = '';
    this.selectedSupportType = this.supportChangePlanEnabled ? 'change-plan' : 'support';
    this.initialSupportType = this.selectedSupportType;
    this.supportSelectedPlanId = null;
    this.fetchSupportPlans();
  }

  closeSupportDialog(force = false) {
    if (!force && this.isSupportFormDirty()) {
      this._confirmationService.confirm({
        header: 'Discard changes?',
        message: 'You have unsaved support request changes. Discard them?',
        acceptLabel: 'Discard',
        rejectLabel: 'Keep Editing',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.resetSupportDialog(),
      });
    } else {
      this.resetSupportDialog();
    }
  }

  private resetSupportDialog() {
    this.supportDialogVisible = false;
    this.supportMessage = '';
    this.selectedSupportType = 'support';
    this.initialSupportType = 'support';
    this.supportSelectedPlanId = null;
    this.supportSelectedInterval = null;
    this.supportRequestError = '';
    this.supportRequestSending = false;
  }

  private isSupportFormDirty(): boolean {
    return (
      this.supportMessage.trim().length > 0 ||
      this.supportSelectedPlanId !== null ||
      this.selectedSupportType !== this.initialSupportType
    );
  }

  isSupportSaveDisabled(): boolean {
    if (this.selectedSupportType !== 'change-plan') {
      return false;
    }

    if (!this.supportSelectedPlanId || !this.supportSelectedInterval) {
      return true;
    }

    const plan = this.selectedSupportPlan;
    return !plan || !this.isIntervalAvailable(plan, this.supportSelectedInterval);
  }

  saveSupportRequest() {
    if (this.supportRequestSending) {
      return;
    }

    this.supportRequestError = '';

    if (this.selectedSupportType === 'change-plan' && !this.supportSelectedPlanId) {
      this.supportRequestError = 'Please select a plan to change to.';
      return;
    }

    if (this.selectedSupportType === 'change-plan' && !this.supportSelectedInterval) {
      this.supportRequestError = 'Please choose a billing interval (monthly or yearly).';
      return;
    }

    const selectedPlan = this.supportPlans.find(
      (p) => p.planId === this.supportSelectedPlanId
    );
    const selectedInterval = this.supportSelectedInterval;

    if (this.selectedSupportType === 'change-plan') {
      if (!selectedPlan || !selectedInterval || !this.isIntervalAvailable(selectedPlan, selectedInterval)) {
        this.supportRequestError =
          'The requested billing interval is not available for the selected plan.';
        return;
      }
    }

    const currentPlanName = this.planData?.planName || 'Unknown plan';
    const currentInterval =
      this.planData?.subscriptionInterval === 12 ? 'Yearly' : 'Monthly';

    const currentBillingAmount = this.currentPlanDetails?.subscription?.totalCost;
    const currentNextCharge = this.currentPlanDetails?.subscription?.nextChargeDate;

    const selectedPrice = selectedInterval && selectedPlan
      ? this.getPlanPriceByInterval(selectedPlan, selectedInterval)
      : null;

    const baseMessage =
      this.selectedSupportType === 'change-plan'
        ? this.buildChangePlanEmailBody({
          currentPlanName,
          currentInterval,
          currentBillingAmount,
          currentNextCharge,
          requestedPlanName: selectedPlan?.title || selectedPlan?.planName || 'N/A',
          requestedInterval: selectedInterval,
          requestedPrice: selectedPrice,
          userNote: this.supportMessage || 'N/A',
        })
        : this.buildSupportEmailBody(this.supportMessage || 'N/A');

    const message = baseMessage;

    this.supportRequestSending = true;
    this._abd.showSpinner();
    this._supportService
      .sendGeneralSupportRequest(
        this.selectedSupportType === 'change-plan'
          ? 'Plan change support request'
          : 'Support request',
        message,
        {
          successMessage:
            'Thanks! We received your request and will contact you shortly.',
        }
      )

      .pipe(
        finalize(() => {
          this.supportRequestSending = false;
          this._abd.hideSpinner();
        })
      )
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.resetSupportDialog();

            const log: CreateSupportLogRequest = {
              userId: this._global.userInfo?.userID,
              fullName: this._global.userInfo?.displayName || '',
              businessId: this._global.userInfo?.businessSetup?.[0]?.businessId,
              businessName: this._global.userInfo?.businessSetup?.[0]?.businessName,
              contactEmail: this._global.userInfo?.email,
              requestType: 'change-plan', // or 'support'
              payload: {
                type: this.selectedSupportType,
                currentPlanName,
                currentInterval,
                currentBillingAmount,
                currentNextCharge,
                requestedPlanName: selectedPlan?.title || selectedPlan?.planName || '',
                requestedInterval: selectedInterval,
                requestedPrice: selectedPrice,
                userNote: this.supportMessage || '',
              },
              notes: this.supportMessage || 'N/A',
            };

            this._supportService.createLog(log).subscribe();
          } else {
            this.supportRequestError =
              'Unable to submit your request right now. Please try again.';
          }
        },
        error: () => {
          this.supportRequestError =
            'Unable to submit your request right now. Please try again.';
        },
      });
  }

  getPlanKey(plan: any): string | number | undefined {
    return plan?.planId ?? plan?.id;
  }

  private getAvailableIntervals(plan: any): Array<'Monthly' | 'Yearly'> {
    if (!plan) return [];
    return this.buildPlanPrices(plan)
      .map((p: any) => p.periodString)
      .filter((p: string) => p === 'Monthly' || p === 'Yearly');
  }

  private getDefaultInterval(plan: any): 'Monthly' | 'Yearly' | null {
    const available = this.getAvailableIntervals(plan);
    if (!available.length) {
      return null;
    }
    if (available.length === 1) {
      return available[0];
    }
    return null;
  }

  selectSupportPlan(plan: any) {
    const availableIntervals = this.getAvailableIntervals(plan);
    this.supportSelectedPlanId = plan?.planId ?? null;

    if (!availableIntervals.includes(this.supportSelectedInterval as any)) {
      this.supportSelectedInterval = this.getDefaultInterval(plan);
    }
  }

  selectSupportInterval(interval: 'Monthly' | 'Yearly') {
    if (!this.supportSelectedPlanId) {
      this.supportRequestError = 'Select a plan before choosing a billing interval.';
      return;
    }

    const plan = this.selectedSupportPlan;
    if (!plan || !this.isIntervalAvailable(plan, interval)) {
      this.supportRequestError =
        'This billing interval is not available for the selected plan.';
      return;
    }

    this.supportSelectedInterval = interval;
    this.supportRequestError = '';
  }

  isIntervalAvailable(plan: any, interval: 'Monthly' | 'Yearly'): boolean {
    if (!plan) return false;
    return !!this.getPlanPriceByInterval(plan, interval);
  }

  hasAnyInterval(plan: any): boolean {
    return this.getAvailableIntervals(plan).length > 0;
  }

  private getPlanPriceByInterval(plan: any, interval: 'Monthly' | 'Yearly') {
    const prices = this.buildPlanPrices(plan);
    return prices.find((p: any) => p.periodString === interval);
  }

  private formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return 'N/A';
    }
    return GlobalService.formatNumberWithCurrencySign(value);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private formatMultiline(value: string): string {
    const escaped = this.escapeHtml(value || '');
    return escaped.replace(/\n/g, '<br />');
  }

  private buildChangePlanEmailBody({
    currentPlanName,
    currentInterval,
    currentBillingAmount,
    currentNextCharge,
    requestedPlanName,
    requestedInterval,
    requestedPrice,
    userNote,
  }: {
    currentPlanName: string;
    currentInterval: string;
    currentBillingAmount: number | null;
    currentNextCharge: string | Date | null;
    requestedPlanName: string;
    requestedInterval: 'Monthly' | 'Yearly' | null;
    requestedPrice: any;
    userNote: string;
  }): string {
    const formattedCurrentAmount = currentBillingAmount
      ? `${this.formatCurrency(currentBillingAmount)} / ${currentInterval.toLowerCase()}`
      : 'N/A';

    const formattedNextCharge = currentNextCharge
      ? new Date(currentNextCharge).toDateString()
      : 'N/A';

    const requestedIntervalText = requestedInterval
      ? `${requestedInterval} (${requestedPrice ? this.formatCurrency(requestedPrice.value) : 'N/A'} per ${requestedInterval.toLowerCase()})`
      : 'N/A';

    const requestedPriceDetails = requestedPrice
      ? `${requestedPrice.priceLimitTypeString || 'N/A'} (${requestedPrice.periodString || requestedInterval || 'N/A'})`
      : 'N/A';

    const rows = [
      { label: 'Business', value: this._global.userInfo?.companyName || 'N/A' },
      { label: 'Requested by', value: this._global.userInfo?.email || 'N/A' },
      { label: 'Current plan', value: `${currentPlanName} (${currentInterval})` },
      { label: 'Current billing', value: formattedCurrentAmount },
      { label: 'Next charge', value: formattedNextCharge },
      { label: 'Requested plan', value: requestedPlanName },
      { label: 'Requested interval', value: requestedIntervalText },
      // { label: 'Requested price details', value: requestedPriceDetails },
    ];

    const tableRows = rows
      .map(
        (row) => `
          <tr>
            <td style="padding: 6px 10px; border: 1px solid #e5e7eb; background: #f9fafb; font-weight: 600; width: 35%;">
              ${this.escapeHtml(row.label)}
            </td>
            <td style="padding: 6px 10px; border: 1px solid #e5e7eb;">
              ${this.escapeHtml(String(row.value || 'N/A'))}
            </td>
          </tr>`
      )
      .join('');

    return `
      <div style="font-family: Arial, sans-serif; color: #111827; font-size: 14px; line-height: 1.6;">
        <p style="margin: 0 0 12px 0;"><strong>Change plan request</strong></p>
        <table style="border-collapse: collapse; width: 100%; max-width: 640px; margin-bottom: 16px;">
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        <p style="margin: 0 0 6px 0;"><strong>User note</strong></p>
        <p style="margin: 0;">${this.formatMultiline(userNote)}</p>
      </div>
    `;
  }

  private buildSupportEmailBody(userNote: string): string {
    return `
      <div style="font-family: Arial, sans-serif; color: #111827; font-size: 14px; line-height: 1.6;">
        <p style="margin: 0 0 12px 0;"><strong>Support request</strong></p>
        <p style="margin: 0 0 6px 0;"><strong>User note</strong></p>
        <p style="margin: 0;">${this.formatMultiline(userNote)}</p>
      </div>
    `;
  }

  togglePlanTags(plan: any) {
    const key = this.getPlanKey(plan);
    if (key === undefined) return;
    if (this.expandedPlanTags.has(key)) {
      this.expandedPlanTags.delete(key);
    } else {
      this.expandedPlanTags.add(key);
    }
  }

  isPlanTagsExpanded(plan: any): boolean {
    const key = this.getPlanKey(plan);
    if (key === undefined) return false;
    return this.expandedPlanTags.has(key);
  }

  getIncludedPlanPackages(plan: any, limit?: number) {
    const packages = plan?.planPackages || plan?.PlanPackages || [];
    const included = packages.filter(
      (pkg: any) => pkg?.included === true || pkg?.included === 'true'
    );

    if (this.isPlanTagsExpanded(plan)) {
      return included;
    }

    if (!limit || included.length <= limit) {
      return included;
    }

    return included.slice(0, limit);
  }

  getIncludedPlanPackagesExtraCount(plan: any, limit: number) {
    const packages = plan?.planPackages || plan?.PlanPackages || [];
    const included = packages.filter(
      (pkg: any) => pkg?.included === true || pkg?.included === 'true'
    );
    if (this.isPlanTagsExpanded(plan)) {
      return 0;
    }
    return included.length > limit ? included.length - limit : 0;
  }


  currentInterval(value: boolean) {
    this.isMonthly = value
    this.currentPlan(false)
    this._signup.selectedPlanInterval = value === true ? "Monthly" : "Yearly"
  }
  goToCheckOut(value: any) {
    let planId = this.plans.filter((v) => v.selected === true)[0]?.planId
    this._signup.selectedPlanDetails = value;
    localStorage.setItem('selectedPlanDetails', JSON.stringify(this._signup.selectedPlanDetails))
    if (this._global.userInfo?.businessSetup[0]?.isFreeTrial) {
      this._router.navigate(["check-out"])
    } else {
      this.selectedPlanForSubscription = this._signup.selectedPlanDetails
      if (value?.planId > planId) {
        this.upgradePlan()
      } else {
        this.downgradePlan()
      }
    }

  }
  goToPricing() {
    window.open("https://lincsell.com/pricing/", "_blank");
  }
  goToSignUP() {
    sessionStorage.setItem('is-Redirect', 'Yes')
    setTimeout(() => {
      this._router.navigateByUrl('signup/check-out')
    }, 100);
  }
  daysLeft() {
    let businessSetup = this._global.userInfo?.businessSetup[0];
    let planInfo = this._global.userInfo?.businessSetup[0]?.subscription;
    this.currentPlanDetails = this._global.userInfo?.businessSetup[0]
    let IsUserOnTrial = businessSetup?.isFreeTrial
    if (IsUserOnTrial) {
      let startDate: Date = new Date();
      let endDate: Date = new Date(businessSetup?.freeTrialEndDate.toString());

      let diff = Math.abs(endDate.getTime() - startDate.getTime());
      return Math.ceil(diff / (1000 * 3600 * 24));

    }
    else {
      let startDate: Date = new Date(businessSetup?.createdDate.toString());
      let endDate: Date = new Date(planInfo?.nextChargeDate.toString());

      let diff = Math.abs(endDate.getTime() - startDate.getTime());
      return Math.ceil(diff / (1000 * 3600 * 24));
    }

  }

  performAction() {
    this.ErrorMessage = '';
    //upgrade subscription
    if (this.CurrentAction == 1) {
      this.upgradeSubPlan();
    } else if (this.CurrentAction == 2) {
      this.downgradeSubPlan();
    }
  }


  upgradePlan() {
    this.ErrorMessage = '';
    this.ShowCustomConfirmation = true;
    this.CurrentAction = 1;
    this.upgradeSubPlan('false');

  }
  downgradePlan() {
    this.ErrorMessage = '';
    this.ShowCustomConfirmation = true;
    this.CurrentAction = 2;
    this.downgradeSubPlan('false');
  }

  upgradeSubPlan(execute = 'true') {
    let Sucess = false;
    try {
      this.ErrorMessage = '';
      this.upgradeSucessMessage = '';
      this._abd.showSpinner();
      let newplan = {
        BuySubscriptionPlanId: this.selectedPlanForSubscription.planId,
      };
      let stripe: boolean = false;
      if (this._global.userInfo.businessSetup[0]?.subscription?.transactionDetails?.paymentMethod === "Stripe") {
        stripe = true
      } else {
        stripe = false
      }
      this._subscriptionPlanService
        .upgradeSubscription(newplan, true, execute, stripe)
        .subscribe((res) => {
          if (res.success) {
            Sucess = true;
            this.subscriptionChangeNotes = res.data.note;
            this.currentSubscriptionCharges =
              res.data.currentSubscriptionCharges;
            this.newSubscriptionCharges =
              res.data.newSubscriptionCharges;
            if (execute == 'true') {
              this.upgradeSucessMessage =
                'You have sucessfully updated your plan.';
              this.getCurrentPlanDetails()
              this.getInvoices()

            }
          } else {
            this.ErrorMessage =
              'Plan change failed, Please try again or contact support!';
          }
        });
    } catch { }
  }
  downgradeSubPlan(execute = 'true') {
    let Sucess = false;
    this.upgradeSucessMessage = '';
    try {
      this.ErrorMessage = '';
      this._abd.showSpinner();
      let newplan = {
        BuySubscriptionPlanId: this.selectedPlanForSubscription.planId,
      };
      let stripe: boolean = false;
      if (this._global.userInfo.businessSetup[0]?.subscription?.transactionDetails?.paymentMethod === "Stripe") {
        stripe = true
      } else {
        stripe = false
      }

      this._subscriptionPlanService
        .upgradeSubscription(newplan, false, execute, stripe)
        .subscribe((res) => {
          if (res.success) {
            Sucess = true;
            this.subscriptionChangeNotes = res.data.note;
            this.currentSubscriptionCharges =
              res.data.currentSubscriptionCharges;
            this.newSubscriptionCharges =
              res.data.newSubscriptionCharges;

            if (execute == 'true') {
              this.upgradeSucessMessage =
                'You have sucessfully updated your plan.';
              this.getCurrentPlanDetails()
              this.getInvoices()
            }
          } else {
            this.ErrorMessage =
              'An error occured during the operation, please try again.';
          }
        });
    } catch {
    } finally {
    }
  }


}
